import { useEffect, useSyncExternalStore } from "react";

/**
 * Registry of "unattended surfaces" — views that run on a screen nobody is
 * sitting in front of (the Production Monitor live wallboard, #1207).
 *
 * A surface registers itself while mounted; RequireAuth reads the flag to decide
 * whether a session that lapses underneath the view should send the screen to
 * the sign-in landing (ordinary pages: yes) or be recovered silently while the
 * last good render stays up (unattended board: yes to the silent recovery).
 *
 * Deliberately a module-level counter rather than context: RequireAuth sits
 * ABOVE the routed page in the tree, so the page cannot provide it.
 */

let holders = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isUnattended(): boolean {
  return holders > 0;
}

/** Register while mounted. Returns nothing — the effect handles release. */
export function useUnattendedSurface(active = true): void {
  useEffect(() => {
    if (!active) return;
    holders += 1;
    emit();
    return () => {
      holders -= 1;
      emit();
    };
  }, [active]);
}

/** True while any unattended surface is mounted. */
export function useIsUnattendedSurface(): boolean {
  return useSyncExternalStore(subscribe, isUnattended, () => false);
}

/**
 * #4974: the routes of the pages that call useUnattendedSurface(), for a
 * reader that must hold from the first frame. The page registers above in an
 * effect after it paints, and its lazy chunk may still be loading, so the
 * install bar and its menu item ask the route during render instead.
 * tests/install-prompt-route-guard-5093.test.ts fails when a caller renders on a route not listed.
 */
export const UNATTENDED_ROUTES: readonly string[] = ["/production-monitor", "/production-monitor/live", "/display"];

/**
 * #5093: `pathname` decoded as React Router decodes it before matching (its
 * decodePath): each segment decoded on its own, a decoded "/" kept as %2F so
 * it never splits a segment, and the path left as it is when any segment's
 * encoding is malformed.
 */
function decodeAsRouter(pathname: string): string {
  try {
    return pathname
      .split("/")
      .map((segment) => decodeURIComponent(segment).replace(/\//g, "%2F"))
      .join("/");
  } catch {
    return pathname;
  }
}

/** Whether `pathname` is one of UNATTENDED_ROUTES, matched as the router does: decoded, any case, trailing slashes ignored. */
export function isUnattendedRoute(pathname: string): boolean {
  const path = decodeAsRouter(pathname).toLowerCase().replace(/(.)\/+$/, "$1");
  return UNATTENDED_ROUTES.includes(path);
}

/** Test/inspection helper — the live holder count. */
export function unattendedSurfaceCount(): number {
  return holders;
}

/**
 * #4836: the other direction. While RequireAuth holds a lapsed session under
 * an unattended surface (the silent recovery above), it says so here, so the
 * surface can tell that reloading now would open the sign-in page with nobody
 * at the screen to sign in. A counter for the same reason as `holders`.
 */
let sessionHolds = 0;

function isSessionHeld(): boolean {
  return sessionHolds > 0;
}

/** RequireAuth: register while it is holding a lapsed session on screen. */
export function useReportSessionHold(held: boolean): void {
  useEffect(() => {
    if (!held) return;
    sessionHolds += 1;
    emit();
    return () => {
      sessionHolds -= 1;
      emit();
    };
  }, [held]);
}

/** True while RequireAuth is holding a lapsed session under an unattended surface. */
export function useIsSessionHeld(): boolean {
  return useSyncExternalStore(subscribe, isSessionHeld, () => false);
}

/** Test/inspection helper: how many RequireAuths are holding a lapsed session. */
export function sessionHoldCount(): number {
  return sessionHolds;
}
